import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { MessageKey } from "common/i18n/message-key";
import { computeResendWait, formatLocalISODateTimeWithZone } from "common/utils/date/date-time";
import { useSearchParams } from "react-router-dom";

import { ProgressBar } from "../components/progress-bar";
import { Button } from "../components/ui/button";
import { useTranslations } from "../hooks/use-translations";
import { apiClient } from "../lib/api";

import type { ProgressBarHandle } from "../components/progress-bar";

export const ResendConfirmationEmailPage = (): React.JSX.Element => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get(`email`) ?? ``;
  const lastSentAt = searchParams.get(`lastSentAt`) ?? ``;
  const retryAfterString = searchParams.get(`retryAfterMinutes`);
  const autoStartParam = searchParams.get(`autoStart`) ?? ``;

  let retryAfterMinutes = 0;

  if (retryAfterString) {
    retryAfterMinutes = Number(retryAfterString);
  }

  const initialWait = useMemo(() => computeResendWait(lastSentAt, retryAfterMinutes), [lastSentAt, retryAfterMinutes]);
  const [missingMinutes, setMissingMinutes] = useState(initialWait.minutes);
  const [missingMilliseconds, setMissingMilliseconds] = useState(initialWait.milliseconds);
  const autoStart = autoStartParam === `true`;
  const { translations } = useTranslations();

  const lastSentTemplate = translations.resendConfirmationEmail.lastSentAt;
  const [beforeDateText, afterDateText] = lastSentTemplate.split(`{date}`);

  let formattedDate = ``;

  if (lastSentAt) {
    const parsed = Date.parse(lastSentAt);

    if (!Number.isNaN(parsed)) {
      formattedDate = formatLocalISODateTimeWithZone(new Date(parsed));
    }
  }

  const addBreakBeforeDate = true;

  const [status, setStatus] = useState(``);
  const [statusVariant, setStatusVariant] = useState(``);
  const [waiting, setWaiting] = useState(autoStart);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  const progressBarRef = useRef<ProgressBarHandle | null>(null);
  const onFinishRef = useRef<(() => Promise<void> | void) | null>(null);

  const handleResend = useCallback(async () => {
    setButtonDisabled(true);
    setWaiting(false);

    const result = await apiClient.resendConfirmationEmail(email);

    if (result.success) {
      setStatus(translations.resendConfirmationEmail.success);
      setStatusVariant(`success`);
    } else {
      const message = translations.auth[result.message] ?? translations.resendConfirmationEmail.failure;

      setStatus(message);
      setStatusVariant(`error`);

      if (result.message === MessageKey.confirmationTooSoon) {
        const details = result.details as { lastSentAt?: string; retryAfterMinutes?: number };

        const search = new URLSearchParams();

        search.set(`email`, email);

        if (details.lastSentAt) {
          search.set(`lastSentAt`, details.lastSentAt);
        }

        if (details.retryAfterMinutes != null) {
          search.set(`retryAfterMinutes`, String(details.retryAfterMinutes));
        }

        search.set(`autoStart`, `true`);

        const newUrl = `${window.location.pathname}?${search.toString()}`;

        window.location.assign(newUrl);
      }
    }
  }, [email, translations]);

  useEffect(() => {
    onFinishRef.current = handleResend;
  }, [handleResend]);

  const onProgressFinish = useCallback(() => {
    const fn = onFinishRef.current;

    if (fn) {
      const res = fn();

      if (res instanceof Promise) {
        res.catch(() => {});
      }
    }
  }, []);

  const handleResendRequest = async () => {
    const { minutes: finalMissingMinutes, milliseconds: finalMissingMilliseconds } = computeResendWait(
      lastSentAt,
      retryAfterMinutes,
    );

    setMissingMinutes(finalMissingMinutes);
    setMissingMilliseconds(finalMissingMilliseconds);

    setWaiting(true);
  };

  useEffect(() => {
    if (waiting) {
      if (!autoStart) {
        progressBarRef.current?.start();
      }
    }
  }, [waiting, autoStart]);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md">
        <h1 className="mb-4 text-center text-2xl" data-testid="page-title">
          {translations.resendConfirmationEmail.title}
        </h1>
        <p className="mb-4 text-center">{translations.resendConfirmationEmail.description}</p>
        {email ? (
          <p className="mb-4 break-words text-center text-lg font-semibold text-blue-700" data-testid="user-email">
            {email}
          </p>
        ) : null}
        {lastSentAt ? (
          <div className="mb-4 text-center">
            {beforeDateText}
            {addBreakBeforeDate ? <br /> : null}
            {formattedDate}
            {afterDateText ?? ``}
          </div>
        ) : null}
        {missingMinutes > 0 && (
          <div className="mb-4 text-center">
            {translations.resendConfirmationEmail.wait.replace(`{minutes}`, String(missingMinutes))}
          </div>
        )}
        {status ? (
          <div
            className={`mb-4 text-center${statusVariant === `success` ? ` text-green-600` : ``}${statusVariant === `error` ? ` text-red-600` : ``}`}
            role="status"
          >
            {status}
          </div>
        ) : null}
        {waiting ? (
          <ProgressBar
            ref={progressBarRef}
            autoStart={autoStart}
            onFinish={onProgressFinish}
            timeout={missingMilliseconds}
            title={translations.resendConfirmationEmail.progress}
          />
        ) : null}
        <Button className="mt-4 w-full" disabled={waiting || buttonDisabled} onClick={handleResendRequest}>
          {translations.resendConfirmationEmail.resendButton}
        </Button>
      </div>
    </div>
  );
};
